import { isBlockedEmailDomain } from './blockedEmailDomains';

// Validation utility functions

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password) => {
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  return {
    isValid: hasMinLength && hasUpperCase && hasNumber && hasSpecialChar,
    hasMinLength,
    hasUpperCase,
    hasNumber,
    hasSpecialChar
  };
};

export const getPasswordStrength = (password) => {
  if (!password) return { strength: 'none', score: 0 };

  const validation = validatePassword(password);
  let score = 0;

  if (validation.hasMinLength) score++;
  if (validation.hasUpperCase) score++;
  if (validation.hasNumber) score++;
  if (validation.hasSpecialChar) score++;
  if (password.length >= 12) score++;

  if (score <= 2) return { strength: 'weak', score, color: '#EF4444' };
  if (score === 3) return { strength: 'fair', score, color: '#F59E0B' };
  if (score === 4) return { strength: 'good', score, color: '#10B981' };
  return { strength: 'strong', score, color: '#059669' };
};

export const validateRequired = (value) => {
  return value && value.trim().length > 0;
};

export const validatePhone = (phone) => {
  if (!phone) return true; // Optional field
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  return phoneRegex.test(phone);
};

export const validateZipCode = (zipCode) => {
  if (!zipCode) return true; // Optional field
  const zipRegex = /^[\d\-\s]+$/;
  return zipRegex.test(zipCode);
};

/**
 * Validates email format and checks if domain is not a public email provider.
 * The blocked-domain list lives in ``utils/blockedEmailDomains.js`` so the
 * lead-magnet landing templates and any other capture form share the same
 * rule. Do NOT inline a second copy here.
 *
 * @param {string} email - Email address to validate
 * @returns {Object} - { isValid: boolean, message?: string }
 */
export const validateWorkEmail = (email) => {
  if (!email) {
    return { isValid: false, message: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }

  if (isBlockedEmailDomain(email)) {
    return { isValid: false, message: 'Please use your work email' };
  }

  return { isValid: true };
};

/**
 * Validates phone number in E.164 format
 * @param {string} phone - Phone number to validate
 * @returns {Object} - { isValid: boolean, message?: string }
 */
export const validatePhoneE164 = (phone) => {
  if (!phone) {
    return { isValid: false, message: 'Phone number is required' };
  }

  // Remove spaces, dashes, and parentheses for validation
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // E.164 format: +[country code][number] (1-15 digits after +)
  const e164Regex = /^\+?[1-9]\d{1,14}$/;
  
  if (!e164Regex.test(cleaned)) {
    return { isValid: false, message: 'Please enter a valid phone number (E.164 format)' };
  }

  return { isValid: true };
};

/**
 * Validates word count for a text field
 * @param {string} text - Text to count words in
 * @param {number} maxWords - Maximum allowed words
 * @param {number} [minWords=0] - Minimum recommended words
 * @returns {Object} - { isValid: boolean, wordCount: number, message?: string }
 */
export const validateWordCount = (text, maxWords, minWords = 0) => {
  if (!text || !text.trim()) {
    return { isValid: false, wordCount: 0, message: 'This field is required' };
  }
  const cleaned = text.trim().replace(/\s+/g, ' ');
  const wordCount = cleaned.split(' ').filter(w => w.length > 0).length;

  if (wordCount > maxWords) {
    return { isValid: false, wordCount, message: `Please keep to ${maxWords} words or fewer.` };
  }
  if (minWords > 0 && wordCount < minWords) {
    return { isValid: false, wordCount, message: `Please write at least ${minWords} words.` };
  }
  return { isValid: true, wordCount };
};

/**
 * Normalize phone number: strip formatting, keep digits and leading +
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/[^\d+]/g, '');
};

/**
 * Count words in a string (collapse whitespace first)
 */
export const countWords = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().replace(/\s+/g, ' ').split(' ').filter(w => w.length > 0).length;
};

